/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import { Flex, Text } from '@chakra-ui/react';
import type { TestContextChoice } from '../../services/demand-source.service';
import type { TestContextState } from '../../hooks/useTestContext';

/**
 * **Test as** — the device and the content a test or a preview runs against (`#979`, `#927 · D1`).
 *
 * The same catalog the Session Simulator runs from, served by `/api/test-contexts`. One per surface:
 * whatever else on the surface runs the rig reads the same {@link TestContextState}, so two pickers
 * on one page would be two controls over one slot.
 *
 * A catalog that fails to load leaves the control hidden and the rig's defaults in force, which is
 * what a test did before this control existed: it still runs.
 */
export const TestContextPicker: React.FC<{ state: TestContextState; disabled: boolean }> = ({ state, disabled }) => {
  const { catalog, selection, setSelection } = state;
  if (!catalog || (catalog.devices.length === 0 && catalog.content.length === 0)) return null;

  return (
    <Flex align="center" gap={1.5} wrap="wrap" role="group" aria-labelledby="test-context-label">
      <Text as="span" fontSize="xs" color="fg.muted" id="test-context-label">
        Test as
      </Text>
      {catalog.devices.length > 0 && (
        <ChoiceSelect
          label="Device"
          choices={catalog.devices}
          value={selection.deviceType ?? catalog.defaults.deviceType ?? ''}
          disabled={disabled}
          onChange={(deviceType) => setSelection({ ...selection, deviceType })}
        />
      )}
      {catalog.content.length > 0 && (
        <ChoiceSelect
          label="Content"
          choices={catalog.content}
          value={selection.contentId ?? catalog.defaults.contentId ?? ''}
          disabled={disabled}
          onChange={(contentId) => setSelection({ ...selection, contentId })}
        />
      )}
    </Flex>
  );
};

const ORIGIN_LABEL: Record<TestContextChoice['origin'], string> = {
  system: 'Built in',
};

/**
 * Grouped by origin only when there is more than one: with one origin a group heading says nothing,
 * and when authored contexts join, the heading is what tells an operator's own context from ours.
 */
const ChoiceSelect: React.FC<{
  label: string;
  choices: readonly TestContextChoice[];
  value: string;
  disabled: boolean;
  onChange: (id: string) => void;
}> = ({ label, choices, value, disabled, onChange }) => {
  const origins = [...new Set(choices.map((choice) => choice.origin))];
  const option = (choice: TestContextChoice) => (
    <option key={`${choice.origin}:${choice.id}`} value={choice.id}>
      {choice.label}
    </option>
  );
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{
        fontSize: '12px',
        padding: '2px 6px',
        borderRadius: '6px',
        border: '1px solid var(--chakra-colors-border-default)',
        background: 'var(--chakra-colors-bg-card)',
        color: 'var(--chakra-colors-fg-default)',
        maxWidth: '220px',
      }}
    >
      {origins.length > 1
        ? origins.map((origin) => (
            <optgroup key={origin} label={ORIGIN_LABEL[origin]}>
              {choices.filter((choice) => choice.origin === origin).map(option)}
            </optgroup>
          ))
        : choices.map(option)}
    </select>
  );
};
